import { useRef, useState, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, Loader2, Settings } from "lucide-react";
import { useVideoJob, getJobStatusMessage } from "../hooks/useVideoJob";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../lib/firebase";

interface OptimizedVideoPlayerProps {
  originalUrl: string;
  jobId?: string;
  mediaId?: string;
  title?: string;
  className?: string;
  onTimeUpdate?: (currentTime: number) => void;
  onDurationChange?: (duration: number) => void;
  isStudentView?: boolean; // Hide processing overlay for students
}

export default function OptimizedVideoPlayer({ 
  originalUrl, 
  jobId,
  mediaId,
  title, 
  className = "",
  onTimeUpdate,
  onDurationChange,
  isStudentView = false
}: OptimizedVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [quality, setQuality] = useState<'720p' | '480p'>('720p');
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [videoCanPlay, setVideoCanPlay] = useState(false);
  const [hasTrackedAccess, setHasTrackedAccess] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const { job, loading } = useVideoJob(jobId || null);
  
  // Debug logging
  useEffect(() => {
    if (job) {
      console.log('📹 Video job status:', {
        jobId,
        status: job.status,
        has720p: !!job.optimizedUrl720p,
        has480p: !!job.optimizedUrl480p,
        originalUrl: job.originalUrl
      });
    }
  }, [job, jobId]);
  
  // Determine which URL to use based on quality selection
  const videoUrl = (() => {
    if (job?.status === 'ready') {
      if (quality === '720p' && job.optimizedUrl720p) {
        return job.optimizedUrl720p;
      } else if (quality === '480p' && job.optimizedUrl480p) {
        return job.optimizedUrl480p;
      } else if (job.optimizedUrl) {
        return job.optimizedUrl; // Fallback for backward compatibility
      }
    }
    return originalUrl;
  })();
  
  // Close quality menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showQualityMenu && !(event.target as Element).closest('.quality-selector')) {
        setShowQualityMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQualityMenu]);
  
  // Preserve playback position when quality changes
  useEffect(() => {
    if (videoRef.current && currentTime > 0) {
      videoRef.current.currentTime = currentTime;
      if (isPlaying) {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [videoUrl]);

  // Reset videoCanPlay when video URL changes
  useEffect(() => {
    setVideoCanPlay(false);
  }, [videoUrl]);

  // Show processing message for instructors when video is being processed
  // For students, hide processing overlay completely
  const showProcessingMessage = !isStudentView && jobId && job && (job.status === 'processing' || job.status === 'uploaded');
  
  // Check if quality selector should be shown
  const hasQualityOptions = job?.status === 'ready' && job.optimizedUrl720p && job.optimizedUrl480p;
  
  // Show info message if video is ready but playing original (not transcoded yet)
  const isPlayingOriginal = !job || job.status !== 'ready' || !job.optimizedUrl720p;

  const trackVideoAccess = async () => {
    if (!mediaId || hasTrackedAccess || !isStudentView) return;
    
    try {
      const functions = getFunctions(app, "us-central1");
      const trackActivity = httpsCallable(functions, "trackMediaActivity");
      await trackActivity({ mediaId, activityType: "access" });
      setHasTrackedAccess(true);
      console.log("✅ Video access tracked:", mediaId);
    } catch (error) {
      console.error("Failed to track video access:", error);
    }
  };

  const resetHideControlsTimer = () => {
    setShowControls(true);
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
    if (isPlaying) {
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 5000);
    }
  };

  const togglePlay = async () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        try {
          // For iOS/iPad: Ensure video is loaded before playing
          if (videoRef.current.readyState < 2) {
            console.log('⏳ Video not ready, waiting for load...');
            // Wait for video to be ready
            await new Promise<void>((resolve) => {
              const checkReady = () => {
                if (videoRef.current && videoRef.current.readyState >= 2) {
                  resolve();
                } else {
                  setTimeout(checkReady, 100);
                }
              };
              checkReady();
            });
          }
          
          // Load video if not loaded yet (important for iOS)
          if (videoRef.current.networkState === 0) {
            console.log('📥 Loading video...');
            videoRef.current.load();
            await new Promise<void>((resolve) => {
              const onLoadedData = () => {
                videoRef.current?.removeEventListener('loadeddata', onLoadedData);
                resolve();
              };
              videoRef.current?.addEventListener('loadeddata', onLoadedData);
            });
          }
          
          console.log('▶️ Playing video...');
          await videoRef.current.play();
          setIsPlaying(true);
          trackVideoAccess();
        } catch (error) {
          console.error('❌ Play failed:', error);
          // iOS Safari might block autoplay, user needs to tap play button
          setIsPlaying(false);
          alert('ไม่สามารถเล่นวิดีโอได้ กรุณาลองอีกครั้ง');
        }
      }
    }
  };

  const handleVideoClick = (e: React.MouseEvent | React.TouchEvent) => {
    // Don't prevent default on iOS/iPad to allow native video controls
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) {
      e.preventDefault();
    }
    
    // Check if device is PC (screen width >= 1024px)
    const isPC = window.innerWidth >= 1024;
    
    if (isPC) {
      // PC: Click to pause/play
      togglePlay();
      resetHideControlsTimer();
    } else {
      // Mobile/Tablet: Click to toggle controls
      if (isPlaying) {
        setShowControls(!showControls);
        if (hideControlsTimeoutRef.current) {
          clearTimeout(hideControlsTimeoutRef.current);
        }
        if (!showControls) {
          hideControlsTimeoutRef.current = setTimeout(() => {
            setShowControls(false);
          }, 5000);
        }
      } else {
        togglePlay();
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        // Try standard fullscreen API first
        if (videoRef.current.requestFullscreen) {
          videoRef.current.requestFullscreen();
        } 
        // Fallback for iOS Safari
        else if ((videoRef.current as any).webkitEnterFullscreen) {
          (videoRef.current as any).webkitEnterFullscreen();
        }
        // Fallback for older browsers
        else if ((videoRef.current as any).mozRequestFullScreen) {
          (videoRef.current as any).mozRequestFullScreen();
        }
        else if ((videoRef.current as any).msRequestFullscreen) {
          (videoRef.current as any).msRequestFullscreen();
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;
      setCurrentTime(time);
      onTimeUpdate?.(time);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const dur = videoRef.current.duration;
      setDuration(dur);
      setVideoCanPlay(true);
      onDurationChange?.(dur);
      console.log('✅ Video loaded:', {
        duration: dur,
        videoWidth: videoRef.current.videoWidth,
        videoHeight: videoRef.current.videoHeight,
        url: videoUrl
      });
    }
  };

  const handleError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    console.error('❌ Video error:', {
      error: video.error,
      code: video.error?.code,
      message: video.error?.message,
      url: videoUrl,
      networkState: video.networkState,
      readyState: video.readyState
    });
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
    resetHideControlsTimer();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Auto-hide controls after 5 seconds when playing
  useEffect(() => {
    if (isPlaying) {
      resetHideControlsTimer();
    } else {
      setShowControls(true);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    }
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [isPlaying]);

  // Show controls on mouse move
  const handleMouseMove = () => {
    if (isPlaying) {
      resetHideControlsTimer();
    }
  };

  return (
    <div 
      className={`relative bg-black rounded-2xl overflow-hidden ${className}`}
      onMouseMove={handleMouseMove}
    >
      {/* Processing Overlay with detailed status */}
      {showProcessingMessage && (
        <div className="absolute inset-0 z-10 bg-black/95 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center px-6 py-8 max-w-md">
            <div className="mb-6">
              <Loader2 className="w-16 h-16 animate-spin text-blue-500 mx-auto" />
            </div>
            
            <h3 className="text-xl font-bold text-white mb-3">
              🎬 กำลังประมวลผลวิดีโอ
            </h3>
            
            <div className="bg-gray-800 rounded-full h-2 overflow-hidden mb-3">
              <div 
                className="bg-gradient-to-r from-blue-500 to-pink-500 h-full transition-all duration-500 animate-pulse"
                style={{ width: job.status === 'processing' ? '60%' : '30%' }}
              />
            </div>
            
            <p className="text-xs text-gray-400">
              {job.status === 'processing' && 'กระบวนการนี้อาจใช้เวลา 2-5 นาที'}
              {job.status === 'uploaded' && 'โปรดรอสักครู่...'}
            </p>
          </div>
        </div>
      )}

      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full aspect-video bg-black cursor-pointer"
        onClick={handleVideoClick}
        onTouchEnd={handleVideoClick}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onError={handleError}
        onLoadStart={() => console.log('🔄 Video loading:', videoUrl)}
        onCanPlay={() => {
          console.log('✅ Video can play');
          setVideoCanPlay(true);
        }}
        onLoadedData={() => console.log('📦 Video data loaded')}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onContextMenu={(e) => e.preventDefault()}
        preload="auto"
        playsInline
        webkit-playsinline="true"
        x5-playsinline="true"
        x-webkit-airplay="allow"
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
        disableRemotePlayback
      >
        <source src={videoUrl} type="video/mp4" />
        <track kind="captions" />
      </video>

      {/* Custom Controls */}
      <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${
        showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}>
        {/* Progress Bar */}
        <input
          type="range"
          min="0"
          max={duration || 0}
          value={currentTime}
          onChange={handleSeek}
          className="w-full mb-3 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-600"
        />

        {/* Controls */}
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                togglePlay();
                resetHideControlsTimer();
              }}
              className="hover:text-blue-400 transition-colors"
            >
              {isPlaying ? (
                <Pause className="w-6 h-6" />
              ) : (
                <Play className="w-6 h-6" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleMute();
                resetHideControlsTimer();
              }}
              className="hover:text-blue-400 transition-colors"
            >
              {isMuted ? (
                <VolumeX className="w-5 h-5" />
              ) : (
                <Volume2 className="w-5 h-5" />
              )}
            </button>
            <span className="text-sm">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {/* Quality Selector */}
            {hasQualityOptions && (
              <div className="relative quality-selector">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowQualityMenu(!showQualityMenu);
                    resetHideControlsTimer();
                  }}
                  className="hover:text-blue-400 transition-colors flex items-center gap-1"
                  title="Quality Settings"
                >
                  <Settings className="w-5 h-5" />
                  <span className="text-xs">{quality}</span>
                </button>
                
                {showQualityMenu && (
                  <div className="absolute bottom-full mb-2 right-0 bg-gray-900 rounded-lg shadow-lg overflow-hidden min-w-[100px]">
                    <button
                      onClick={() => { 
                        setQuality('720p'); 
                        setShowQualityMenu(false);
                      }}
                      className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-800 transition-colors ${
                        quality === '720p' ? 'text-blue-400 bg-gray-800' : 'text-white'
                      }`}
                    >
                      720p HD
                    </button>
                    <button
                      onClick={() => { 
                        setQuality('480p'); 
                        setShowQualityMenu(false);
                      }}
                      className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-800 transition-colors ${
                        quality === '480p' ? 'text-blue-400 bg-gray-800' : 'text-white'
                      }`}
                    >
                      480p SD
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFullscreen();
                resetHideControlsTimer();
              }}
              className="hover:text-blue-400 transition-colors"
            >
              <Maximize className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Play Overlay */}
      {!isPlaying && !showProcessingMessage && (
        <div
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer group"
        >
          <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center group-hover:bg-white transition-all">
            <Play className="w-10 h-10 text-blue-600 ml-1" />
          </div>
        </div>
      )}

      {/* Transcoding Info Banner */}
      {jobId && job && job.status === 'ready' && !hasQualityOptions && (
        <div className="absolute top-4 left-4 right-4 bg-blue-500/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>วิดีโอกำลังประมวลผลเพื่อปรับความคมชัด กรุณารอสักครู่...</span>
        </div>
      )}
    </div>
  );
}
